import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  InputAdornment
} from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';       // ¡NUEVO!
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // ¡NUEVO!
import api from './api';

// Initial state for a new user
const initialFormData = {
  id: null,
  name: '',
  lastNamePaternal: '',
  lastNameMaternal: '',
  username: '',
  status: false,
  role: '',
  plantas: [],
  availablePlants: [], // This will be fetched on mount for new users
  creationDate: '',
  updateDate: '',
  contacto: '',
  password: ''
};

function UserDetailForm({ open, onClose, user, onSave }) {
  const [formData, setFormData] = useState({
    id: user?.id || null,
    name: user?.name || '',
    lastNamePaternal: user?.lastNamePaternal || '',
    lastNameMaternal: user?.lastNameMaternal || '',
    username: user?.username || '',
    status: user?.status || false,
    role: user?.role || '',
    plantas: user?.plantas || [],
    availablePlants: user?.availablePlants || [],
    creationDate: user?.creationDate || '',
    updateDate: user?.updateDate || '',
    contacto: user?.contacto || '',
    password: ''
  });

  const [checkedAvailable, setCheckedAvailable] = useState([]);
  const [checkedAssigned, setCheckedAssigned] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({}); //estado para los errores

  //campos obligatorios
  const baseRequiredFields = [
    'name',
    'lastNamePaternal',
    'lastNameMaternal',
    'username',
    'role',
  ];

  // Añade 'password' a los campos obligatorios solo si es un usuario nuevo
  const dynamicRequiredFields = user
    ? baseRequiredFields
    : [...baseRequiredFields, 'password'];

  useEffect(() => {
    if (open) { // Only do something if the modal is actually open
      if (user) { // Editing an existing user
        api.get('/plantas/?skip=0&limit=1000').then(res => {
          const allPlants = res.data
            .filter(p => p.is_active)
            .sort((a, b) => a.nombre.localeCompare(b.nombre));

          const assignedPlantIds = user.plantas.map(pl => typeof pl === 'object' ? pl.id : pl);

          const assignedPlants = allPlants.filter(p => assignedPlantIds.includes(p.id));
          const availablePlants = allPlants.filter(p => !assignedPlantIds.includes(p.id));

          setFormData({
            id: user.id,
            name: user.nombre || '',
            lastNamePaternal: user.a_paterno || '',
            lastNameMaternal: user.a_materno || '',
            username: user.username || '',
            status: user.is_active || false,
            role: user.role || '',
            contacto: user.contacto || '',
            password: '', // Password is not pre-filled for security
            plantas: assignedPlants,
            availablePlants: availablePlants,
            creationDate: user.created_at || '',
            updateDate: user.updated_at || '',
          });

          setCheckedAssigned([]);
          setCheckedAvailable([]);
        }).catch(err => {
          console.error('Error al cargar plantas para usuario existente:', err);
          // Fallback if plants loading fails
          setFormData(prevData => ({
              ...prevData,
              id: user.id,
              name: user.nombre || '',
              lastNamePaternal: user.a_paterno || '',
              lastNameMaternal: user.a_materno || '',
              username: user.username || '',
              status: user.is_active || false,
              role: user.role || '',
              contacto: user.contacto || '',
              password: '', // Password is not pre-filled for security
              plantas: user.plantas || [], // Use existing plantas if fetching fails
              availablePlants: [],
              creationDate: user.created_at || '',
              updateDate: user.updated_at || '',
          }));
        });
      } else { // Creating a new user
        setFormData(initialFormData); // Reset to initial empty state
        setCheckedAssigned([]);
        setCheckedAvailable([]);

        // Fetch all available plants for a new user
        api.get('/plantas/?skip=0&limit=1000').then(res => {

          const sortedPlants = res.data
          .filter(p => p.is_active)
          .sort((a, b) => a.nombre.localeCompare(b.nombre)); 
          setFormData((prevData) => ({
            ...prevData,
            availablePlants: sortedPlants // Only active plants
          }));
        }).catch(err => {
          console.error('Error al cargar plantas para nuevo usuario:', err);
          setFormData((prevData) => ({
            ...prevData,
            availablePlants: [] 
          }));
        });
      }
      setShowPassword(false);
      setErrors({});
    }
  }, [open, user]); 



  useEffect(() => {
    const fetchPlantas = async () => {
      try {
        const res = await api.get('/plantas/?skip=0&limit=1000');
        const plantasDisponibles = res.data.filter(p => p.is_active);
        setFormData((prevData) => ({
          ...prevData,
          availablePlants: plantasDisponibles
        }));
      } catch (err) {
        console.error('Error al cargar plantas:', err);
      }
    };

    if (!user) fetchPlantas();
  }, [user]);


  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    // Limpiar el error si el usuario empieza a escribir/seleccionar en el campo
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: false,
      }));
    }

    // Si el rol cambia, también limpiamos el error de 'plantas' por si cambia de un rol que requiere plantas a uno que no
    if (name === 'role') {
      setErrors(prevErrors => ({
        ...prevErrors,
        plantas: false,
      }));
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    const newErrors = {};
    let isValid = true;

    // Campos obligatorios dinámicos
    const currentRequiredFields = [...dynamicRequiredFields];
    

    // Lógica para hacer 'contacto' obligatorio si el rol es supervisor o coordinador
    if (formData.role === 'supervisor' || formData.role === 'coordinador') {
      currentRequiredFields.push('contacto');
    }

    currentRequiredFields.forEach(field => {
      const value = formData[field];
      // Para TextFields (cadenas): verifica si está vacío o solo con espacios
      if (typeof value === 'string' && value.trim() === '') {
        newErrors[field] = true;
        isValid = false;
      }
      // Para Selects (pueden tener valores numéricos o strings): verifica si es null, undefined, 0 o cadena vacía
      else if (value === null || value === undefined || value === '' || value === 0) {
        newErrors[field] = true;
        isValid = false;
      }

      
    });

    // --- NUEVA LÓGICA DE VALIDACIÓN PARA EL CAMPO 'PLANTAS' ---
    const rolesRequierenPlantas = ['operator', 'tecnico'];
    if (rolesRequierenPlantas.includes(formData.role) && formData.plantas.length === 0) {
      newErrors.plantas = true;
      isValid = false;
    }

    setErrors(newErrors); // Actualiza el estado de errores para re-renderizar

    if (!isValid) {
      console.error('Campos obligatorios vacíos:', newErrors);
      return; // ¡IMPORTANTE! Detiene la ejecución aquí si hay errores de validación
    }

    // Si la validación pasa, procede con el guardado


    try {
      const payload = {
        nombre: formData.name,
        a_paterno: formData.lastNamePaternal,
        a_materno: formData.lastNameMaternal,
        username: formData.username,
        is_active: formData.status,
        role: formData.role,
        contacto: formData.contacto,
        plantas: formData.plantas.map(p => p?.id).filter(Boolean),
      };

      if (formData.id && formData.password.trim() !== '') {
          payload.password = formData.password;
        }


      if (formData.id) {
        await api.put(`/users/${formData.id}`, payload);
      } else {
        payload.password = formData.password;
        await api.post('/users/', payload);
      }

      onSave(formData);
      onClose();
    } catch (error) {
          console.error('Error al guardar usuario:', error);

          const backendMessage =
            error.response?.data?.detail || 'Ocurrió un error inesperado';

          alert(`Error al guardar usuario:\n${backendMessage}`);
        }
  };

  const handleCancel = () => {
    setCheckedAssigned([]);
    setCheckedAvailable([]);
    setErrors({}); // Limpia también los errores
    setShowPassword(false);
    onClose();
  };

  // Función para alternar la visibilidad de la contraseña
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault(); // Evita que se pierda el foco al hacer click rápido
  };

  const roles = ['admin', 'operator', 'coordinador', 'supervisor', 'tecnico'];

  const handleToggleAvailable = (value) => () => {
    const currentIndex = checkedAvailable.indexOf(value);
    const newCheckedAvailable = [...checkedAvailable];

    if (currentIndex === -1) {
      newCheckedAvailable.push(value);
    } else {
      newCheckedAvailable.splice(currentIndex, 1);
    }
    setCheckedAvailable(newCheckedAvailable);
  };

  const handleToggleAssigned = (value) => () => {
    const currentIndex = checkedAssigned.indexOf(value);
    const newCheckedAssigned = [...checkedAssigned];

    if (currentIndex === -1) {
      newCheckedAssigned.push(value);
    } else {
      newCheckedAssigned.splice(currentIndex, 1);
    }
    setCheckedAssigned(newCheckedAssigned);
  };

  // Asignar plantas
  const handleMoveToAssigned = () => {
    const newplantas = [...formData.plantas, ...checkedAvailable.map(index => formData.availablePlants[index])];
    const newAvailablePlants = formData.availablePlants.filter((_, index) => !checkedAvailable.includes(index));
    setFormData({ ...formData, plantas: newplantas, availablePlants: newAvailablePlants });
    setCheckedAvailable([]);
  };

  // Liberar plantas
  const handleMoveToAvailable = () => {
    const newAvailablePlants = [...formData.availablePlants, ...checkedAssigned.map(index => formData.plantas[index])];
    const newplantas = formData.plantas.filter((_, index) => !checkedAssigned.includes(index));
    setFormData({ ...formData, plantas: newplantas, availablePlants: newAvailablePlants });
    setCheckedAssigned([]);
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Detalle de Usuario</Typography>
          <Typography variant="body2" color="text.secondary">Usuarios &gt; Detalle</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Nombre" name="name" value={formData.name} onChange={handleChange} variant="outlined" size="small"
              required error={!!errors.name} helperText={errors.name ? 'Este campo es obligatorio' : ''} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Apellido paterno" name="lastNamePaternal" value={formData.lastNamePaternal} onChange={handleChange} variant="outlined" size="small"
              required error={!!errors.lastNamePaternal}
              helperText={errors.lastNamePaternal ? 'Este campo es obligatorio' : ''} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Apellido materno" name="lastNameMaternal" value={formData.lastNameMaternal} onChange={handleChange} variant="outlined" size="small"
              required error={!!errors.lastNameMaternal}
              helperText={errors.lastNameMaternal ? 'Este campo es obligatorio' : ''} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="User" name="username" value={formData.username} onChange={handleChange} variant="outlined" size="small"
              required error={!!errors.username}
              helperText={errors.username ? 'Este campo es obligatorio' : ''} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Contraseña"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              variant="outlined"
              size="small"
              required={!user}
              error={!!errors.password}
              helperText={errors.password ? 'Este campo es obligatorio' : ''}
              InputProps={{ // Propiedad para añadir adornos al input
                endAdornment: ( // Adorno al final del input
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={<Switch checked={formData.status} onChange={handleChange} name="status" color="primary" />}
              label="Estatus"
              labelPlacement="start"
              sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
            />

          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Contacto" name="contacto" value={formData.contacto} onChange={handleChange} variant="outlined" size="small"
              required={formData.role === 'supervisor' || formData.role === 'coordinador'}
              error={!!errors.contacto}
              helperText={errors.contacto ? 'Este campo es obligatorio para el rol seleccionado' : ''} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth variant="outlined" size="small"
              required error={!!errors.role}>
              <InputLabel>Rol</InputLabel>
              <Select label="Rol" name="role" value={formData.role} onChange={handleChange}>
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
              {errors.role && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  Este campo es obligatorio
                </Typography>
              )}
            </FormControl>
          </Grid>

          {formData.role !== 'admin' && (
            <Grid size={12}>
              <Typography variant="subtitle1" gutterBottom>Plantas</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 5 }}>
                  <List dense sx={{ width: '100%', height: 200, overflow: 'auto', border: '1px solid #ccc', borderColor: errors.plantas ? 'red' : '#ccc' }}>
                    {formData.availablePlants.map((plant, index) => (
                      <ListItem key={plant} onClick={handleToggleAvailable(index)} button>
                        <ListItemIcon>
                          <Checkbox checked={checkedAvailable.indexOf(index) !== -1} edge="start" disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={plant.nombre} />
                      </ListItem>
                    ))}
                  </List>
                  {errors.plantas && ( 
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      Debes asignar al menos una planta para operadores y tecnicos.
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IconButton onClick={handleMoveToAssigned} disabled={checkedAvailable.length === 0}><ArrowForward /></IconButton>
                  <IconButton onClick={handleMoveToAvailable} disabled={checkedAssigned.length === 0}><ArrowBack /></IconButton>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <List dense sx={{ width: '100%', height: 200, overflow: 'auto', border: '1px solid #ccc' }}>
                    {formData.plantas.map((plant, index) => (
                      <ListItem key={plant} onClick={handleToggleAssigned(index)} button>
                        <ListItemIcon>
                          <Checkbox checked={checkedAssigned.indexOf(index) !== -1} edge="start" disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={plant.nombre} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
          )}

          {formData.id && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Fecha de Creación" name="creationDate" value={formData.creationDate} onChange={handleChange} variant="outlined" size="small" disabled />
            </Grid>
          )}

          {formData.id && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Fecha actualización" name="updateDate" value={formData.updateDate} onChange={handleChange} variant="outlined" size="small" disabled />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserDetailForm;
